/**
 * Per-CARD campus scope for Settings > My School.
 *
 * ## Why this exists
 *
 * The My-School page reads ORG-level: the header badges the organization, not
 * a campus, and there is deliberately NO page-level campus dropdown (Sheila,
 * 2026-08-24 — the dropdown "didn't impact anything"; and #2138's stacking of
 * every card per campus was rejected outright: "Undo separating settings page
 * by location").
 *
 * But most of the cards on that page are **per-campus writes** — the OVS
 * component split, milk shares, the auto-order switch, dietary notes,
 * student name order, the school-account change requests. #2143 dropped the
 * dropdown and anchored every one of them to `schools[0]`, so in a
 * multi-campus district only the alphabetically-first campus was visible or
 * editable and every save landed there regardless of intent. Districts
 * reported it as "OVS not working" and "I don't have permission to edit it"
 * (there is no permission gate — their campus was simply unreachable).
 *
 * The shape Sheila asked for: *"should just be all org info, if campus by
 * campus, can breakdown within each section"*. So the page stays org-level
 * and each genuinely per-campus card carries its OWN compact campus control
 * in its header — the breakdown lives inside the section, not above the page.
 *
 * ## Invariants
 *
 * 1. **A single-campus org renders exactly as it did before.** `scopeControl`
 *    is `null` when the admin administers one campus, and `CardTitleRow`
 *    renders its children UNWRAPPED in that case — same DOM, no extra chrome.
 * 2. **Scope is per section.** Each `CampusScope` owns its own selection, so
 *    switching campus inside the milk card does not move the dietary-notes
 *    card. Sharing one selection across every card would be the rejected
 *    page-level dropdown wearing several triggers.
 * 3. **The control must ride along every render branch of its card**
 *    (loading, error, empty, loaded). A picker that disappears while a query
 *    settles strands the admin on whichever campus is selected.
 */

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ordo/ui/select'
import { type ReactNode, useState } from 'react'

import type { AdminSchool } from '@/api/school-orders'

/**
 * The ONE campus-resolution rule for scoped settings cards: the explicit
 * selection when it still exists in the list, else the first campus.
 *
 * Falling back to the first campus is correct HERE (it is a default view, not
 * a write target) — the bug #2143 shipped was having no way to leave it.
 */
export function resolveScopedCampus(schools: AdminSchool[], selectedId: string | null): AdminSchool | null {
  if (selectedId) {
    const found = schools.find((s) => s.id === selectedId)
    if (found) {
      return found
    }
  }
  return schools[0] ?? null
}

export interface CampusScopeSelectProps {
  schools: AdminSchool[]
  value: string
  onChange: (id: string) => void
  /** Names the section, so each card's control is distinguishable to AT. */
  label: string
}

/** Compact, header-sized campus picker. Lives INSIDE a card header. */
export function CampusScopeSelect({ schools, value, onChange, label }: CampusScopeSelectProps) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-8 w-full max-w-56 text-xs sm:w-56" aria-label={`Campus for ${label}`}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {schools.map((s) => (
          <SelectItem key={s.id} value={s.id}>
            {s.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

export interface CampusScopeState {
  /** The campus this section is scoped to; null only when the admin has none. */
  school: AdminSchool | null
  /** The header control to render, or null for a single-campus org. */
  scopeControl: ReactNode
  /** Re-scope the section. Unknown ids fall back to the first campus. */
  selectCampus: (id: string | null) => void
}

/**
 * One section's campus scope. `CampusScope` is the render-prop form; use the
 * hook directly when a section needs to re-scope itself programmatically.
 */
export function useCampusScope(schools: AdminSchool[], label: string): CampusScopeState {
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const school = resolveScopedCampus(schools, selectedId)
  const scopeControl =
    school && schools.length > 1 ? (
      <CampusScopeSelect schools={schools} value={school.id} onChange={setSelectedId} label={label} />
    ) : null
  return { school, scopeControl, selectCampus: setSelectedId }
}

export interface CampusScopeProps {
  schools: AdminSchool[]
  /** Section name, forwarded to the control's accessible label. */
  label: string
  children: (args: { school: AdminSchool; scopeControl: ReactNode }) => ReactNode
}

/**
 * Holds one section's campus selection and hands the section both the
 * selected campus and the control to render in its header.
 *
 * Render-prop rather than a wrapping layout element so the card's own
 * `<Card>` stays the outermost node — no extra DOM around the section.
 * ONE `useState` per instance, so no hook is ever called in a loop: only the
 * selected campus's card mounts, never one card per campus.
 */
export function CampusScope({ schools, label, children }: CampusScopeProps) {
  const { school, scopeControl } = useCampusScope(schools, label)
  if (!school) {
    return null
  }
  return <>{children({ school, scopeControl })}</>
}

/**
 * Card-header title row. With no `scopeControl` (single-campus org) it is a
 * pass-through and the header DOM is byte-identical to the pre-scope page.
 */
export function CardTitleRow({ scopeControl, children }: { scopeControl?: ReactNode; children: ReactNode }) {
  if (!scopeControl) {
    return <>{children}</>
  }
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0 flex-1">{children}</div>
      {scopeControl}
    </div>
  )
}
